import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FuelCheckbox, FuelRadio, FuelTypography } from '@frete.com/fuel-react';
import { debounce, get, set } from 'lodash';
import {
  usePlateSearch,
  useTruckerAutoFillVerify,
  useCitiesFilter,
  useRNTRCSearch,
  useSearchParamUUID,
  useEditFocusHandler,
  useZipcode,
} from '@/components/FormWrapper/hooks';
import { string, validate } from '@/utils/validations';
import { useAppContext } from '@/context';
import { FormContainer, FormItem, FormRow, LoadingInput, LoadingInputWrapper } from '@/components/FormWrapper/Steps';
import { InlineRadioGroup, Input, Select, Separator } from '@/components';
import { makeTruckCompositionOwner, makeTruckCompositions, useIsDisabledField } from './functions';
import schema from './validation';
import { makeVehicleColorsList } from '@/data';
import * as utils from '@/utils';
import { CityModel, DeepPartial, Flatten, FormDeepKeys, FormTruckComposition, FormTrucker } from '@/types';
import { DynamicObject, EnabledFields, Errors } from './types';
import { useFreight, useToast, useTracker } from '@/hooks';
import * as services from '@/services';
import { getCityModelByAddressModel } from '@/services/utils';
import { getFreightService } from '@/services/get-freight-service';
import { CellphoneInput } from '@/components/input-cellphone-anonymize/input-cellphone-anonymized';

export const Compositions: React.FC = () => {
  const [errorsState, setErrors] = useState<Errors>();
  const { dispatch, state } = useAppContext();
  const searchUUID = useSearchParamUUID();
  const tracker = useTracker();
  const { freightId } = useFreight();
  const [loadingCpfCnpj, setLoadingCpfCnpj] = useState<boolean[]>([]);
  const [enabledFields, setEnabledFields] = useState<EnabledFields>({});
  const toast = useToast();
  const errorFieldHandler = utils.useErrorFieldHandler(dispatch);
  const citiesFilter = useCitiesFilter();
  const form = useMemo(() => ({ ...state.risk.data }), [state.risk.data]);
  const triedToSubmit = useMemo(() => state.risk.triedToSubmit, [state.risk.triedToSubmit]);
  const truckTypes = useMemo(() => [...state.truckTypes.data], [state.truckTypes.data]);
  const vehicleColors = useMemo(makeVehicleColorsList, []);
  const setFormValue = utils.useSetFormValue(dispatch);
  const autoFillVerify = useTruckerAutoFillVerify(state);
  const { searchPlate, loadingPlate } = usePlateSearch(form, truckTypes);
  const { searchRNTRC, loadingRNTRC } = useRNTRCSearch(form, setEnabledFields);
  const { handleFocus, handleBlur } = useEditFocusHandler();
  const { onUpdateZipcode, isLoadingZipcode } = useZipcode({ type: 'multiple' });
  const { isDisabledField, isPlateNotFilled, isResubmit } = useIsDisabledField();

  const verifyOwnerFill = (index: number, field: keyof Flatten<FormTrucker>) =>
    form.truck.compositions[index].truckerIsOwner && autoFillVerify(field);

  const compositions = useMemo(
    () => form.truck.compositions.map((_, index) => index),
    [form.truck.compositions.length],
  );

  const truckSubtypes = useMemo(() => {
    const type = truckTypes.find(({ id }) => id === form.truck.typeId);
    return type?.subtypes || [];
  }, [form.truck.typeId, truckTypes]);

  const setTruckType = (value: string) => {
    const type = truckTypes.find(({ id }) => id === value);

    if (!type || type.id === form.truck.typeId) return;

    setFormValue('truck.compositions', makeTruckCompositions(type.compositions));
    setFormValue('truck.typeName', type.name);
    setFormValue('truck.typeId', type.id);
  };

  const setTruckSubtype = (position: number, value: string) => {
    const subtype = truckSubtypes.find(({ id }) => id === value);
    if (!subtype) return;

    setFormValue(`truck.compositions.${position}.typeName`, subtype.name);
    setFormValue(`truck.compositions.${position}.typeId`, subtype.id);
  };

  const setTruckerIsOwner = (position: number) => {
    const { ...composition } = form.truck.compositions[position];
    composition.truckerIsOwner = !composition.truckerIsOwner;
    composition.owner = makeTruckCompositionOwner(composition.truckerIsOwner ? form.trucker : undefined);

    setFormValue(`truck.compositions.${position}`, composition);
  };

  const onUpdateOwnerName = (position: number, value: string) => {
    setFormValue(`truck.compositions.${position}.owner.name`, value);
    const { owner } = form.truck.compositions[position];
    if (!owner.rntrcOwnerIsTruckOwner || (owner.rntrcOwnerIsTruckOwner && owner.rntrcName === value)) return;
    setFormValue(`truck.compositions.${position}.owner.rntrcName`, value);
  };

  const setFieldEnabledState = (path: FormDeepKeys, value: boolean) =>
    setEnabledFields((previousValue) => set(previousValue, path, value));

  const setFieldValueAndEnabledState = (path: FormDeepKeys, value: string | CityModel) => {
    const initialValue = path.includes('location') ? {} : '';
    setFormValue(path, value || initialValue);
    setFieldEnabledState(path, !value);
  };

  const verifyIfIsEnabledByCpfCnpj = (path: FormDeepKeys) => {
    if (searchUUID) return true;
    return !!get(enabledFields, path);
  };

  const fillTruckerDataByCpfCnpj = useCallback(
    debounce(async (value: string, position: number) => {
      try {
        setLoadingCpfCnpj(() => Array.from(Array(compositions.length), (_, index) => index === position));

        const { address, name = '', motherName = '', birthdate = '' } = await services.loadTruckerDataByDocument(value);
        const location = getCityModelByAddressModel({ city: address.city, state: address.state });

        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.name`, name);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.motherName`, motherName);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.birthdate`, utils.dateYmdToDmy(birthdate));
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.zipCode`, address.zipCode);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.number`, address.number);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.neighborhood`, address.neighborhood);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.complement`, address.complement);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.street`, address.street);
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.location`, location);

        if (name) toast.add({ type: 'success', title: 'Dados encontrados com sucesso!' });
      } catch {
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.name`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.motherName`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.birthdate`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.zipCode`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.number`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.neighborhood`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.complement`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.street`, '');
        setFieldValueAndEnabledState(`truck.compositions.${position}.owner.address.location`, undefined);
      } finally {
        setLoadingCpfCnpj((old) => old.map((item, index) => (index === position ? false : item)));
      }
    }, 1000),
    [compositions],
  );

  const getCompositionWithEqualCnpjCpf = (position: number, value: string) => {
    const compositionWithEqualCnpjCpf = form.truck.compositions.find(
      ({ owner: { cpfCnpj, rntrcCpfCnpj } }, index) =>
        (cpfCnpj === value || rntrcCpfCnpj === value) && position !== index,
    );

    return compositionWithEqualCnpjCpf;
  };

  const getCompositionCpfCnpjIsEqual = (composition: DeepPartial<FormTruckComposition>, value: string) =>
    composition?.owner?.cpfCnpj === value;

  const getIsAbleToVerifyCpfCnpj = (composition: DeepPartial<FormTruckComposition>, value: string) => {
    const isAbleToVerifyCpfCnpj =
      [11, 14].includes(value.length) &&
      value !== form.trucker.cpf &&
      !getCompositionCpfCnpjIsEqual(composition, value);

    return isAbleToVerifyCpfCnpj;
  };

  const onUpdateOwnerCpfCnpj = async (position: number, value: string) => {
    let { ...composition } = form.truck.compositions[position];
    if (!value) {
      setFormValue(`truck.compositions.${position}.owner`, {
        cellPhone: '',
        cpfCnpj: '',
        name: '',
        rntrc: '',
        rntrcUuid: '',
        rntrcCpfCnpj: '',
        rntrcName: '',
        rntrcOwnerIsTruckOwner: false,
        address: {
          complement: '',
          neighborhood: '',
          number: '',
          street: '',
          zipCode: '',
        },
      });
    }
    if (value === form.trucker.cpf) {
      composition.truckerIsOwner = true;
      composition.owner = makeTruckCompositionOwner(form.trucker);

      setFormValue(`truck.compositions.${position}`, composition);
    } else if (form.truck.compositions[position].truckerIsOwner) {
      composition.truckerIsOwner = false;
      composition.owner = makeTruckCompositionOwner();

      setFormValue(`truck.compositions.${position}`, composition);
    }

    setFormValue(`truck.compositions.${position}.owner.cpfCnpj`, value);
    const { owner } = form.truck.compositions[position];

    if (owner.rntrcOwnerIsTruckOwner || owner.rntrcCpfCnpj === value) {
      setFormValue(`truck.compositions.${position}.owner.rntrcCpfCnpj`, value);
    }

    const compositionWithEqualCnpjCpf = getCompositionWithEqualCnpjCpf(position, value);
    const isAbleToVerifyCpfCnpj = getIsAbleToVerifyCpfCnpj(compositionWithEqualCnpjCpf, value);

    if (isAbleToVerifyCpfCnpj) {
      setErrors(set({}, `truck.compositions.${position}.owner.cpfCnpj`, undefined));
      try {
        const localSchema = string().cpfOrCnpj().required();
        localSchema.validateSync(value);
        fillTruckerDataByCpfCnpj(value, position);
      } catch (error) {
        setErrors(set({}, `truck.compositions.${position}.owner.cpfCnpj`, error.message));
      }
    }

    if (getCompositionCpfCnpjIsEqual(compositionWithEqualCnpjCpf, value)) {
      setErrors(set({}, `truck.compositions.${position}.owner.cpfCnpj`, undefined));
      setFormValue(`truck.compositions.${position}.owner`, structuredClone(compositionWithEqualCnpjCpf.owner));
      setEnabledFields((previousValue) =>
        structuredClone(set(previousValue, `truck.compositions.${position}.owner`, {})),
      );

      if (compositionWithEqualCnpjCpf.truckerIsOwner) {
        composition = { ...form.truck.compositions[position] };
        composition.truckerIsOwner = compositionWithEqualCnpjCpf.truckerIsOwner;
        composition.owner = makeTruckCompositionOwner(form.trucker);

        setFormValue(`truck.compositions.${position}`, composition);
      }
    } else if (
      compositionWithEqualCnpjCpf?.owner?.rntrcCpfCnpj &&
      compositionWithEqualCnpjCpf?.owner?.rntrcCpfCnpj === value
    ) {
      setFormValue(`truck.compositions.${position}.owner.name`, compositionWithEqualCnpjCpf.owner.rntrcName);
    }
  };

  const setupStep = () => {
    setTruckType(form.truck.typeId);
  };

  const showCompositionSeparator = (position: number): boolean => position + 1 !== compositions.length;

  const isLoading = (loading: boolean[], position: number) => {
    if (loading.length < position + 1) return false;
    return loading[position];
  };

  const clearFieldRNTRC = (position: number) => {
    setFormValue(`truck.compositions.${position}.owner.rntrc`, '');
    setFormValue(`truck.compositions.${position}.owner.rntrcCpfCnpj`, '');
    setFormValue(`truck.compositions.${position}.owner.rntrcName`, '');
  };

  const handleUpdateRNTRC = useCallback(
    debounce((RNTRC: string, position: number) => searchRNTRC(RNTRC, position), 1_500),
    [],
  );

  const getCleanCpfCnpj = (composition: FormTruckComposition) => {
    const { cpfCnpj, rntrcCpfCnpj } = composition.owner;
    if (rntrcCpfCnpj && !rntrcCpfCnpj.includes('*')) return rntrcCpfCnpj;
    if (cpfCnpj && !cpfCnpj.includes('*')) return cpfCnpj;
    return '';
  };

  const searchPlaceDoneCallback = (position: number, composition: FormTruckComposition) => {
    const cpfCnpj = getCleanCpfCnpj(composition);

    onUpdateOwnerCpfCnpj(position, cpfCnpj).then(() => {
      setFormValue(`truck.compositions.${position}.owner.rntrc`, composition.owner.rntrc);
      searchRNTRC(composition.owner.rntrc, position);
    });
  };

  const onUpdatePlate = async (plate: string, position: number) => {
    setFormValue(`truck.compositions.${position}.plate`, plate);
    searchPlate(plate, position, searchPlaceDoneCallback);

    if (plate.length < 8) {
      clearFieldRNTRC(position);

      if (!!form.truck.compositions[position].location) {
        setFormValue(`truck.compositions.${position}.location`, undefined);
      }
    }
  };

  const setRntrcOwnerIsTruckOwner = (position: number) => {
    const { ...composition } = form.truck.compositions[position];
    const localEnabledFields = structuredClone(enabledFields);

    set(localEnabledFields, `truck.compositions.${position}.owner.rntrcCpfCnpj`, false);
    set(localEnabledFields, `truck.compositions.${position}.owner.rntrcName`, false);

    setEnabledFields(localEnabledFields);

    setFormValue(`truck.compositions.${position}.owner.rntrcCpfCnpj`, composition.owner.cpfCnpj);
    setFormValue(`truck.compositions.${position}.owner.rntrcName`, composition.owner.rntrcName);
  };

  const onUpdateCpfCnpjRntrc = (position: number, value: string) => {
    if (value.includes('*')) return;

    if (value === form.truck.compositions[position].owner.cpfCnpj) {
      setRntrcOwnerIsTruckOwner(position);
    }

    if (value === form.trucker.cpf) {
      setFormValue(`truck.compositions.${position}.owner.rntrcName`, form?.trucker?.name);
    }

    const compositionWithEqualCnpjCpf = form.truck.compositions.find(
      ({ owner: { cpfCnpj, rntrcCpfCnpj } }) => cpfCnpj === value || rntrcCpfCnpj === value,
    );

    if (compositionWithEqualCnpjCpf?.owner?.cpfCnpj === value) {
      setFormValue(`truck.compositions.${position}.owner.rntrcName`, compositionWithEqualCnpjCpf.owner.name);
    } else if (compositionWithEqualCnpjCpf?.owner?.rntrcCpfCnpj === value) {
      setFormValue(`truck.compositions.${position}.owner.rntrcName`, compositionWithEqualCnpjCpf.owner.rntrcName);
    }

    setFormValue(`truck.compositions.${position}.owner.rntrcCpfCnpj`, value);
  };

  const handleFreightData = async () => {
    if (!freightId) return;

    const {
      data: { cargoType, originCity, originState, destinationCity, destinationState },
    } = await getFreightService(freightId);
    setFormValue('travel', {
      cargoType,
      cargoValue: '',
      originPlace: {
        name: originCity,
        place: `${originCity} - ${originState}`,
        stateAbbr: originState,
      },
      destinationPlace: {
        name: destinationCity,
        place: `${destinationCity} - ${destinationState}`,
        stateAbbr: destinationState,
      },
    });
  };

  const handleUpdateMotherName = (position: number, value: string | number) => {
    setFormValue(`truck.compositions.${position}.owner.motherName`, value);

    const currentComposition = form.truck.compositions[position];

    form.truck.compositions.forEach((composition, index) => {
      if (composition.owner.cpfCnpj === currentComposition.owner.cpfCnpj && index !== position) {
        setFormValue(`truck.compositions.${index}.owner.motherName`, value);
      }
    });
  };

  useEffect(() => {
    const errors = validate<Errors>(schema, form);
    if (triedToSubmit) {
      setErrors(errors);
      errorFieldHandler(errors as DynamicObject);
    }
  }, [triedToSubmit, form]);

  useEffect(() => {
    if (triedToSubmit) {
      dispatch({ action: 'risk_set_is_valid', payload: !Boolean(errorsState) });
    }
  }, [errorsState]);

  useEffect(() => {
    setupStep();
    handleFreightData();
    tracker.dispatchWithUserData('risk_management_form_vehicle_viewed');
  }, []);

  return (
    <FormContainer>
      <FormRow>
        <FormItem>
          <Select
            label="Modelo/Composição"
            onUpdate={(value) => setTruckType(value.toString())}
            optionLabel="alias"
            optionValue="id"
            emitValue
            options={truckTypes}
            placeholder="Selecione a composição do veículo"
            value={form.truck.typeId}
            error={errorsState?.truck?.typeId}
            name="truck.typeId"
            disabled={isResubmit}
          />
        </FormItem>
      </FormRow>
      {compositions.map((position) => {
        const {
          typeId,
          renavam,
          plate,
          chassis,
          brand,
          model,
          color,
          monofactureYear,
          modelYear,
          location,
          vehicleIsLeased,
          leaserCpfCnpj,
          truckerIsOwner,
          owner,
        } = form.truck.compositions[position];

        return (
          <Fragment key={position}>
            <FormRow>
              <FormItem>
                <LoadingInputWrapper>
                  <Input
                    autoFocus
                    label="Placa"
                    mask={utils.getMask('plate')}
                    unmaskedValue
                    onUpdate={(value) => onUpdatePlate(value.toString(), position)}
                    placeholder="AAA 0A000"
                    value={plate}
                    error={errorsState?.truck?.compositions?.[position]?.plate}
                    name={`truck.compositions.${position}.plate`}
                    disabled={isResubmit}
                  />
                  {isLoading(loadingPlate, position) && <LoadingInput />}
                </LoadingInputWrapper>
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <Select
                  label="Tipo"
                  onUpdate={(value) => setTruckSubtype(position, value.toString())}
                  optionLabel="alias"
                  optionValue="id"
                  emitValue
                  options={truckSubtypes}
                  value={typeId}
                  error={errorsState?.truck?.compositions?.[position]?.typeId}
                  name={`truck.compositions.${position}.typeId`}
                  disabled={isDisabledField({ position, field: 'typeId', loadingPlate })}
                />
              </FormItem>

              <FormItem>
                <Input
                  label="Renavam"
                  mask={utils.getMask('renavam', renavam.includes('*'))}
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.renavam`, value)}
                  placeholder="00000000000"
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  value={renavam}
                  error={errorsState?.truck?.compositions?.[position]?.renavam}
                  name={`truck.compositions.${position}.renavam`}
                  disabled={isDisabledField({ position, field: 'renavam', loadingPlate })}
                />
              </FormItem>
              <FormItem>
                <Input
                  label="Chassi"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.chassis`, value)}
                  value={chassis}
                  error={errorsState?.truck?.compositions?.[position]?.chassis}
                  name={`truck.compositions.${position}.chassis`}
                  disabled={isDisabledField({ position, field: 'chassis', loadingPlate })}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <Input
                  label="Marca"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.brand`, value)}
                  value={brand}
                  error={errorsState?.truck?.compositions?.[position]?.brand}
                  name={`truck.compositions.${position}.brand`}
                  disabled={isDisabledField({ position, field: 'brand', loadingPlate })}
                />
              </FormItem>
              <FormItem>
                <Input
                  label="Modelo"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.model`, value)}
                  value={model}
                  error={errorsState?.truck?.compositions?.[position]?.model}
                  name={`truck.compositions.${position}.model`}
                  disabled={isDisabledField({ position, field: 'model', loadingPlate })}
                />
              </FormItem>
              <FormItem>
                <Select
                  label="Cor"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.color`, value)}
                  options={vehicleColors}
                  scrollable
                  value={color}
                  error={errorsState?.truck?.compositions?.[position]?.color}
                  name={`truck.compositions.${position}.color`}
                  disabled={isDisabledField({ position, field: 'color', loadingPlate })}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <Input
                  label="Ano de fabricação"
                  mask={utils.getMask('year')}
                  placeholder="0000"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.monofactureYear`, value)}
                  value={monofactureYear}
                  error={errorsState?.truck?.compositions?.[position]?.monofactureYear}
                  name={`truck.compositions.${position}.monofactureYear`}
                  disabled={isDisabledField({ position, field: 'monofactureYear', loadingPlate })}
                />
              </FormItem>
              <FormItem>
                <Input
                  label="Ano modelo"
                  mask={utils.getMask('year')}
                  placeholder="0000"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.modelYear`, value)}
                  value={modelYear}
                  error={errorsState?.truck?.compositions?.[position]?.modelYear}
                  name={`truck.compositions.${position}.modelYear`}
                  disabled={isDisabledField({ position, field: 'modelYear', loadingPlate })}
                />
              </FormItem>
              <FormItem sm={4} md={2} lg={2}>
                <Select
                  label="Cidade"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.location`, value)}
                  optionLabel="place"
                  optionValue="place"
                  options={[]}
                  filter={citiesFilter}
                  placeholder="Digite para buscar a cidade"
                  scrollable
                  noContent={utils.cityNoContent}
                  noArrow
                  filterOnFocus
                  lazy
                  inputAutoFocus
                  debounceFilter={350}
                  value={location}
                  error={errorsState?.truck?.compositions?.[position]?.location}
                  name={`truck.compositions.${position}.location`}
                  disabled={!isResubmit && isPlateNotFilled(position, loadingPlate)}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <InlineRadioGroup label="Veículo é arrendado?">
                  <FuelRadio
                    label="Sim"
                    onFuel-change={() => setFormValue(`truck.compositions.${position}.vehicleIsLeased`, true)}
                    checked={vehicleIsLeased}
                    disabled={isPlateNotFilled(position, loadingPlate)}
                  />
                  <FuelRadio
                    label="Não"
                    onFuel-change={() => setFormValue(`truck.compositions.${position}.vehicleIsLeased`, false)}
                    checked={!vehicleIsLeased}
                    disabled={isPlateNotFilled(position, loadingPlate)}
                  />
                </InlineRadioGroup>
              </FormItem>
              {vehicleIsLeased && (
                <>
                  <FormItem>
                    <Input
                      label="CPF/CNPJ do arrendatário"
                      mask={utils.getMask('cpfOrCnpj')}
                      unmaskedValue
                      onUpdate={(value) => setFormValue(`truck.compositions.${position}.leaserCpfCnpj`, value)}
                      placeholder="000.000.000-00"
                      value={leaserCpfCnpj}
                      error={errorsState?.truck?.compositions?.[position]?.leaserCpfCnpj}
                      name={`truck.compositions.${position}.leaserCpfCnpj`}
                      disabled={isPlateNotFilled(position, loadingPlate)}
                    />
                  </FormItem>
                  <FormItem>
                    <Input
                      label="Nome/Razão social do arrendatário"
                      onUpdate={(value) =>
                        setFormValue(
                          `truck.compositions.${position}.leaserName`,
                          form.truck.compositions[position].leaserCpfCnpj.length <= 11
                            ? (value as string).replace(/\d/g, '')
                            : value,
                        )
                      }
                      value={form.truck.compositions[position].leaserName}
                      error={errorsState?.truck?.compositions?.[position]?.leaserName}
                      name={`truck.compositions.${position}.leaserName`}
                      disabled={isPlateNotFilled(position, loadingPlate)}
                    />
                  </FormItem>
                </>
              )}
            </FormRow>

            <FormRow>
              <FuelTypography
                tag="h3"
                size="heading-sm"
                weight="bold"
                color="neutral-strong"
                spacingBottom={12}
                spacingTop={24}
              >
                Proprietário do veículo (CRLV)
              </FuelTypography>
            </FormRow>
            <FormRow>
              <FormItem sm={4} md={4} lg={4}>
                <FuelCheckbox
                  onFuel-change={() => setTruckerIsOwner(position)}
                  checked={truckerIsOwner}
                  label="O motorista é proprietário do veículo"
                  disabled={isPlateNotFilled(position, loadingPlate)}
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <LoadingInputWrapper>
                  <Input
                    label="CPF/CNPJ"
                    mask={utils.getMask('cpfOrCnpj')}
                    unmaskedValue
                    onUpdate={(value) => onUpdateOwnerCpfCnpj(position, value.toString())}
                    placeholder="000.000.000-00"
                    value={owner.cpfCnpj}
                    error={errorsState?.truck?.compositions?.[position]?.owner?.cpfCnpj}
                    name={`truck.compositions.${position}.owner.cpfCnpj`}
                    disabled={verifyOwnerFill(position, 'cpf') || isPlateNotFilled(position, loadingPlate)}
                  />
                  {isLoading(loadingCpfCnpj, position) && <LoadingInput />}
                </LoadingInputWrapper>
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem sm={4} md={2} lg={2}>
                <Input
                  label="Nome ou Razão social"
                  onUpdate={(value) =>
                    onUpdateOwnerName(
                      position,
                      owner.cpfCnpj.length <= 11 ? (value.toString() as string).replace(/\d/g, '') : value.toString(),
                    )
                  }
                  value={owner.name}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.name}
                  name={`truck.compositions.${position}.owner.name`}
                  disabled={
                    verifyOwnerFill(position, 'name') ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.name`)
                  }
                />
              </FormItem>
              <FormItem>
                <CellphoneInput
                  label="Telefone"
                  mask={utils.getMask('cellPhone')}
                  unmaskedValue
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.cellPhone`, value)}
                  placeholder="(00) 00000-0000"
                  value={owner.cellPhone}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.cellPhone}
                  name={`truck.compositions.${position}.owner.cellPhone`}
                  disabled={isPlateNotFilled(position, loadingPlate)}
                  anonymizeOnBlur
                  applyMaskCheckOwner={truckerIsOwner}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </FormItem>
            </FormRow>
            {owner.cpfCnpj.length <= 11 && (
              <FormRow>
                <FormItem>
                  <Input
                    label="Data de nascimento"
                    onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.birthdate`, value)}
                    type="date"
                    value={owner.birthdate}
                    error={errorsState?.truck?.compositions?.[position]?.owner?.birthdate}
                    name={`truck.compositions.${position}.owner.birthdate`}
                    minDate={utils.birthdateRange().min}
                    maxDate={utils.birthdateRange().max}
                    placeholder="DD/MM/AAAA"
                    disabled={
                      verifyOwnerFill(position, 'birthday') ||
                      isPlateNotFilled(position, loadingPlate) ||
                      !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.birthdate`)
                    }
                  />
                </FormItem>
                <FormItem sm={4} md={2} lg={2}>
                  <Input
                    label="Nome da mãe"
                    placeholder="Nome completo da mãe"
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onUpdate={(value) => handleUpdateMotherName(position, value)}
                    value={owner.motherName}
                    error={errorsState?.truck?.compositions?.[position]?.owner?.motherName}
                    name={`truck.compositions.${position}.owner.motherName`}
                    disabled={
                      verifyOwnerFill(position, 'motherName') ||
                      isPlateNotFilled(position, loadingPlate) ||
                      !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.motherName`)
                    }
                  />
                </FormItem>
              </FormRow>
            )}
            <FormRow>
              <LoadingInputWrapper>
                <Input
                  label="CEP"
                  mask={utils.getMask('zipCode')}
                  unmaskedValue
                  onUpdate={(value) => onUpdateZipcode((value || '').toString(), position)}
                  placeholder="00000-000"
                  value={owner.address.zipCode}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.address?.zipCode}
                  name={`truck.compositions.${position}.owner.address.zipCode`}
                  disabled={
                    verifyOwnerFill(position, 'address.zipCode') ||
                    isLoadingZipcode(position) ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.address.zipCode`)
                  }
                />
                {isLoadingZipcode(position) && <LoadingInput />}
              </LoadingInputWrapper>
            </FormRow>
            <FormRow>
              <FormItem>
                <Input
                  label="Endereço"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.address.street`, value)}
                  placeholder="Insira o endereço"
                  value={owner.address.street}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.address?.street}
                  name={`truck.compositions.${position}.owner.address.street`}
                  disabled={
                    verifyOwnerFill(position, 'address.street') ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.address.street`)
                  }
                />
              </FormItem>
              <FormItem>
                <Input
                  mask={utils.getMask('addressNumber')}
                  label="Número (opcional)"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.address.number`, value)}
                  placeholder="0000"
                  value={owner.address.number}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.address?.number}
                  name={`truck.compositions.${position}.owner.address.number`}
                  disabled={
                    verifyOwnerFill(position, 'address.number') ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.address.number`)
                  }
                />
              </FormItem>
              <FormItem>
                <Input
                  label="Complemento (opcional)"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.address.complement`, value)}
                  placeholder="Caso exista complemento"
                  value={owner.address.complement}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.address?.complement}
                  name={`truck.compositions.${position}.owner.address.complement`}
                  disabled={
                    verifyOwnerFill(position, 'address.complement') ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.address.complement`)
                  }
                />
              </FormItem>
              <FormItem>
                <Input
                  label="Bairro (opcional)"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.address.neighborhood`, value)}
                  placeholder="Insira o bairro"
                  value={owner.address.neighborhood}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.address?.neighborhood}
                  name={`truck.compositions.${position}.owner.address.neighborhood`}
                  disabled={
                    verifyOwnerFill(position, 'address.neighborhood') ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.address.neighborhood`)
                  }
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem sm={4} md={2} lg={2}>
                <Select
                  label="Cidade"
                  onUpdate={(value) => setFormValue(`truck.compositions.${position}.owner.address.location`, value)}
                  optionLabel="place"
                  optionValue="place"
                  options={[]}
                  filter={citiesFilter}
                  placeholder="Digite para buscar a cidade"
                  scrollable
                  noContent={utils.cityNoContent}
                  noArrow
                  filterOnFocus
                  lazy
                  inputAutoFocus
                  debounceFilter={350}
                  value={owner.address.location}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.address?.location}
                  name={`truck.compositions.${position}.owner.address.location`}
                  disabled={
                    verifyOwnerFill(position, 'address.location') ||
                    isPlateNotFilled(position, loadingPlate) ||
                    !verifyIfIsEnabledByCpfCnpj(`truck.compositions.${position}.owner.address.location`)
                  }
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FuelTypography
                tag="h3"
                size="heading-sm"
                weight="bold"
                color="neutral-strong"
                spacingBottom={12}
                spacingTop={24}
              >
                Dados RNTRC
              </FuelTypography>
            </FormRow>
            <FormRow>
              <FormItem>
                <LoadingInputWrapper>
                  <Input
                    label="RNTRC"
                    mask={utils.getMask('rntrc')}
                    onUpdate={(value) => {
                      if (!isResubmit) handleUpdateRNTRC(value.toString(), position);
                      setFormValue(`truck.compositions.${position}.owner.rntrc`, value);
                    }}
                    placeholder="000000000"
                    value={owner.rntrc}
                    error={errorsState?.truck?.compositions?.[position]?.owner?.rntrc}
                    name={`truck.compositions.${position}.owner.rntrc`}
                    disabled={isPlateNotFilled(position, loadingPlate)}
                  />
                  {isLoading(loadingRNTRC, position) && <LoadingInput />}
                </LoadingInputWrapper>
              </FormItem>
            </FormRow>

            <FormRow>
              <FormItem>
                <Input
                  label="CPF/CNPJ do proprietário do RNTRC"
                  mask={utils.getMask('cpfOrCnpj', owner?.rntrcCpfCnpj?.includes('*'))}
                  unmaskedValue={!owner.rntrcCpfCnpj?.includes('*')}
                  onUpdate={(value) => onUpdateCpfCnpjRntrc(position, value.toString())}
                  placeholder="000.000.000-00"
                  value={owner.rntrcCpfCnpj}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.rntrcCpfCnpj}
                  name={`truck.compositions.${position}.owner.rntrcCpfCnpj`}
                  disabled={!isResubmit && isPlateNotFilled(position, loadingPlate)}
                />
              </FormItem>
              <FormItem md={2} lg={2}>
                <Input
                  label="Nome/Razão social do proprietário do RNTRC"
                  onUpdate={(value) =>
                    setFormValue(
                      `truck.compositions.${position}.owner.rntrcName`,
                      owner.rntrcCpfCnpj.length <= 11 ? (value as string).replace(/\d/g, '') : value,
                    )
                  }
                  value={owner.rntrcName}
                  error={errorsState?.truck?.compositions?.[position]?.owner?.rntrcName}
                  name={`truck.compositions.${position}.owner.rntrcName`}
                  disabled={!isResubmit && isPlateNotFilled(position, loadingPlate)}
                />
              </FormItem>
            </FormRow>

            {showCompositionSeparator(position) && (
              <FormItem sm={4} md={4} lg={4}>
                <Separator verticalMargin="1rem" />
              </FormItem>
            )}
          </Fragment>
        );
      })}
    </FormContainer>
  );
};
